import { Fragment } from 'react';
import { parseReply, type InlineToken } from '../../services/assistant/replyParser';
import type { NavTargetKey } from '../../services/assistant/navTargets';

interface InlineProps {
  tokens: InlineToken[];
  onNavigate: (target: NavTargetKey) => void;
}

function Inline({ tokens, onNavigate }: InlineProps) {
  return (
    <>
      {tokens.map((t, i) => {
        if (t.kind === 'bold') {
          return (
            <strong key={i} className="font-semibold text-black dark:text-white">
              {t.text}
            </strong>
          );
        }
        if (t.kind === 'nav') {
          return (
            <button
              key={i}
              type="button"
              onClick={() => onNavigate(t.target)}
              className="font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary dark:text-primary-lighter"
            >
              {t.label}
            </button>
          );
        }
        return <Fragment key={i}>{t.text}</Fragment>;
      })}
    </>
  );
}

interface AssistantReplyProps {
  content: string;
  onNavigate: (target: NavTargetKey) => void;
}

/** Renders a reply as paragraphs and lists. Never uses innerHTML. */
export default function AssistantReply({ content, onNavigate }: AssistantReplyProps) {
  const blocks = parseReply(content);
  return (
    <div className="space-y-2 break-words">
      {blocks.map((block, bi) => {
        if (block.kind === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <ListTag key={bi} className={`space-y-1 pl-5 ${block.ordered ? 'list-decimal' : 'list-disc'} marker:text-primary`}>
              {block.items.map((item, ii) => (
                <li key={ii}>
                  <Inline tokens={item} onNavigate={onNavigate} />
                </li>
              ))}
            </ListTag>
          );
        }
        return (
          <p key={bi}>
            {block.lines.map((line, li) => (
              <Fragment key={li}>
                {li > 0 && <br />}
                <Inline tokens={line} onNavigate={onNavigate} />
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
